// Site id → name lookup for agent-facing surfaces. Agents can share access to
// multiple sites, so the monitor, live-chat rows, and chat detail label which
// site a visitor/conversation belongs to. Sourced from /api/admin/sites, which
// returns only the sites the caller belongs to (all sites for a super-admin).

import { useEffect } from 'react';
import type { CSSProperties } from 'react';
import { create } from 'zustand';
import { apiFetch } from '../lib/api';

export interface SiteLite {
  id: string;
  name: string;
  slug: string;
  /** Widget theme colors, so agent-facing site labels can carry the site's own
   *  brand. accentColor = badge background, primaryColor = badge text. */
  accentColor?: string | null;
  primaryColor?: string | null;
}

/**
 * Inline style for a site-name badge tinted with the site's widget theme:
 * accent color as the fill, the widget's text (primary) color as the label.
 * Returns undefined when the site/colors are unknown so callers fall back to
 * the neutral CSS-class styling.
 */
export function siteBadgeStyle(site?: SiteLite | null): CSSProperties | undefined {
  if (!site || (!site.accentColor && !site.primaryColor)) return undefined;
  return {
    backgroundColor: site.accentColor ?? undefined,
    color: site.primaryColor ?? undefined,
    borderColor: site.accentColor ?? undefined,
  };
}

interface SitesState {
  byId: Map<string, SiteLite>;
  loaded: boolean;
  loading: boolean;
  /** Fetch once (idempotent). Safe to call from many components. */
  ensureLoaded: () => void;
}

export const useSitesStore = create<SitesState>((set, get) => ({
  byId: new Map(),
  loaded: false,
  loading: false,
  ensureLoaded: () => {
    const s = get();
    if (s.loaded || s.loading) return;
    set({ loading: true });
    apiFetch<{ sites: SiteLite[] }>('/api/admin/sites')
      .then((r) => {
        set({ byId: new Map(r.sites.map((x) => [x.id, x])), loaded: true, loading: false });
      })
      .catch(() => set({ loading: false }));
  },
}));

/**
 * Reactive site id → name map. Triggers the one-time fetch on first use and
 * re-renders when it lands. Returns a `nameFor` helper for convenience.
 */
export function useSiteNames(): {
  byId: Map<string, SiteLite>;
  nameFor: (siteId?: string | null) => string | null;
} {
  const byId = useSitesStore((s) => s.byId);
  const ensureLoaded = useSitesStore((s) => s.ensureLoaded);
  useEffect(() => {
    ensureLoaded();
  }, [ensureLoaded]);
  return {
    byId,
    nameFor: (siteId) => (siteId ? (byId.get(siteId)?.name ?? null) : null),
  };
}
